// features/projects/templates/professional/widgets/confidence-movement.js
// ═══════════════════════════════════════════════════════════
// CONFIDENCE MOVEMENT — Tier-A widget
//
// Q: "Which findings are gaining or losing defensibility?"
// Renders each substantive finding with a horizontal bar +/-
// indicator showing its current strength score (Phase A status,
// confidence tier, effect-size shape).
//
// The reference layout shows literal week-over-week deltas. We
// don't track historical confidence yet, so v1 derives a
// "movement score" from current state: phase-A coverage gives
// positive movement; unreviewed substantive findings give
// negative movement; respecified is neutral. Real deltas land
// when we add a confidence-history table in a later session.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { WidgetShell, CaveatStrip } from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function ConfidenceMovementWidget() {
  const { loading, comparisons } = useWidgetData();
  const movements = useMemo(() => composeMovements(comparisons), [comparisons]);

  if (loading) {
    return <WidgetShell label="Confidence Movement"><Loading /></WidgetShell>;
  }
  if (movements.length === 0) {
    return (
      <WidgetShell label="Confidence Movement">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No movements yet. Findings will appear here once they have verdicts and Phase A status.
        </div>
      </WidgetShell>
    );
  }

  const maxAbs = Math.max(...movements.map(m => Math.abs(m.score)), 12);

  return (
    <WidgetShell label="Confidence Movement">
      <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.55, marginBottom: 12 }}>
        Direction of defensibility per finding — positive when Phase A surviving, negative when claims sit unverified at scale.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {movements.map(m => <MovementRow key={m.id} movement={m} maxAbs={maxAbs} />)}
      </div>
      <CaveatStrip text="v1 derives movement from current state, not historical week-over-week shifts. Real deltas arrive when confidence-history tracking ships." />
    </WidgetShell>
  );
}

function MovementRow({ movement, maxAbs }) {
  const positive = movement.score > 0;
  const neutral = movement.score === 0;
  const color = neutral ? L.dim : positive ? L.sage : L.copper;
  const widthPct = (Math.abs(movement.score) / maxAbs) * 50; // 50% max half-width

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 50px',
      alignItems: 'center', gap: 12,
      padding: '6px 0',
    }}>
      <div style={{
        fontSize: 12, color: L.text, fontFamily: FONT.sans, fontWeight: 500,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {movement.label}
      </div>
      {/* Bar — centered axis with positive bars going right, negative going left */}
      <div style={{
        position: 'relative',
        height: 6, background: L.s3, borderRadius: 1,
      }}>
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: '50%', width: 1, background: L.b3,
        }} />
        {!neutral && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            background: color, opacity: 0.85,
            ...(positive
              ? { left: '50%', width: `${widthPct}%` }
              : { right: '50%', width: `${widthPct}%` }
            ),
          }} />
        )}
      </div>
      <div style={{
        fontFamily: FONT.mono, fontSize: 11, color, fontWeight: 600,
        textAlign: 'right', letterSpacing: '0.04em',
      }}>
        {neutral ? '0' : (positive ? '+' : '') + movement.score}
      </div>
    </div>
  );
}

function composeMovements(comparisons) {
  const substantive = (comparisons || []).filter(c =>
    c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase())
  );
  return substantive
    .map(c => {
      const dyn = DYN_BY_KEY[c.dynamic_key];
      // Movement score derived from current state:
      //  +12 survived Phase A
      //  +5  survived-respecified (precision gain, but claim narrowed)
      //  +8  HIGH confidence + verdict, no Phase A yet (data is solid)
      //  -3  Phase A killed
      //  -6  DIVERGENT, no Phase A (unverified domain-specificity claim)
      //  -3  any substantive finding without Phase A
      let score = 0;
      if (c.phase_a_status === 'survived') score = 12;
      else if (c.phase_a_status === 'survived-respecified') score = 5;
      else if (c.phase_a_status === 'killed') score = -3;
      else if ((c.verdict || '').toUpperCase() === 'DIVERGENT') score = -6;
      else if ((c.confidence || '').toUpperCase() === 'HIGH') score = 8;
      else score = -3;
      return {
        id: c.id,
        label: dyn?.label || c.dynamic_key,
        score,
      };
    })
    .sort((a, b) => b.score - a.score);
}

function Loading() {
  return <div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div>;
}
